import type { Cart, LineItem } from '@app/data/cart-bff';
import {
  AnalyticsContext,
  AnalyticsPageTypeProperty,
  Item as GAItem,
  RbGtmClient,
} from '@redbubble/next-analytics/gtm';
import { round } from 'lodash';

export enum GACategory {
  Cart = 'Cart',
  CartMessage = 'Cart Messages',
  CheckoutFlow = 'CheckoutFlow',
}

export enum GAAction {
  CheckoutStartApplePay = 'CheckoutStart-ApplePay',
  CheckoutStartGooglePay = 'CheckoutStart-GooglePay',
  Click = 'click',
  Complete = 'Complete',
  CouponAutoApplied = 'CouponAutoApplied',
  CouponLoginButtonClicked = 'CouponLoginButtonClicked',
  CouponManuallyApplied = 'CouponManuallyApplied',
  CouponManuallyAppliedError = 'CouponManuallyAppliedError',
  CouponModalClosed = 'CouponModalClosed',
  CouponModalOpened = 'CouponModalOpened',
  CouponRemove = 'CouponRemove',
  CouponRemoved = 'CouponRemoved',
  CouponSignUpButtonClicked = 'CouponSignUpButtonClicked',
  InView = 'in-view',
  LineItemRemoved = 'ItemRemoved',
  PaymentAvailableApplePay = 'PaymentAvailable-ApplePay',
  PaymentAvailableGooglePay = 'PaymentAvailable-GooglePay',
  ProductAttributeSelected = 'ProductAttributeSelected',
  ShippingPreferenceChanged = 'Shipping Preference Changed',
}

export enum GALabel {
  ApplePay = 'ApplePay',
  Cart = 'Cart',
  GooglePay = 'GooglePay',
  Quantity = 'quantity',
}

/**
 * GA4 events
 */
export type CartLineItemForGA4 = Pick<
  LineItem,
  | 'work'
  | 'details'
  | 'total'
  | 'quantity'
  | 'gaCode'
  | 'undiscountedTotal'
  | 'isDigital'
>;

type GALineItem = Pick<LineItem, 'work' | 'details' | 'isDigital'>;

const isGAGiftCertificateItem = (item: GALineItem): boolean => {
  return (
    item?.isDigital === true &&
    item?.details?.type?.startsWith('Gift Card') === true
  );
};

const getGAGiftCertificateItemCode = ({ details: { price } }: GALineItem) => {
  return `gift-certificate-${price?.currency}-${price?.amount}`;
};

const getGAItemId = (item: GALineItem) => {
  if (isGAGiftCertificateItem(item)) {
    return getGAGiftCertificateItemCode(item);
  }

  // TODO[PEX-1312]: update monolith cart API to include the item_id analytics field ,
  // do we do not neet to generate the `item_id` here (similar to inventory API)
  return `${item.work?.id}_${item.details.productTypeId}_${item.details.blankItemId}`;
};

const getItemDiscount = (
  item: Pick<LineItem, 'undiscountedTotal' | 'total'>
) => {
  const discount =
    (item.undiscountedTotal?.amount || 0) - (item.total?.amount || 0);

  return round(discount, 2);
};

export function mapCartLineItemToAnalyticsItem(
  cartLineItem: CartLineItemForGA4,
  index: number | null,
  coupon: string
): GAItem & { coupon: string };
export function mapCartLineItemToAnalyticsItem(
  cartLineItem: CartLineItemForGA4,
  index: number | null
): GAItem;

export function mapCartLineItemToAnalyticsItem(
  cartLineItem: CartLineItemForGA4,
  index: number | null,
  coupon?: string
): GAItem & { coupon?: string } {
  const { details, work, quantity, gaCode } = cartLineItem;
  return {
    item_id: getGAItemId(cartLineItem),
    inventory_item_id: getGAItemId(cartLineItem),
    product_type_id: details.productTypeId || '',
    blank_item_id: details.blankItemId || '',
    work_id: work?.id || '',
    retargeting_id: details.marketingProductTypeId || '',
    index,
    price: details.price?.amount || 0,
    discount: getItemDiscount(cartLineItem),
    quantity,
    item_list_id: '',
    item_list_name: '',
    ...(gaCode && { item_category5: gaCode }),
    ...(coupon !== undefined && { coupon }),
  };
}

export const trackGA4ViewCart = ({
  cart,
  currency,
}: {
  cart: Cart;
  currency: string;
}) => {
  const { appliedCoupon, summary, lineItems } = cart;
  const { estimatedCost } = summary.shipping.selected;

  RbGtmClient.track({
    event: 'view_cart',
    page_location: window.location.toString(),
    currency,
    ecommerce: {
      coupon: appliedCoupon?.code || '',
      currency,
      value: summary.total.amount.toString(),
      items: lineItems.map((item, i) =>
        mapCartLineItemToAnalyticsItem(item, i)
      ),
      shipping: summary.showTaxSeparately
        ? estimatedCost.amountExTax
        : estimatedCost.price.amount,
    },
  });
};

export const trackGA4RemoveLineItemFromCart = ({
  lineItem,
  currency,
  quantity,
  pageType,
}: {
  lineItem: CartLineItemForGA4;
  currency: string;
  quantity: number;
  pageType: AnalyticsPageTypeProperty;
}) => {
  const lineItemRemovedTotal = (
    quantity * (lineItem.details.price?.amount || 0)
  ).toFixed(2);

  RbGtmClient.track({
    event: 'remove_from_cart',
    page_location: window.location.toString(),
    page_type: pageType,
    context: AnalyticsContext.QuantitySelector,
    ecommerce: {
      currency,
      value: lineItemRemovedTotal,
      items: [{ ...mapCartLineItemToAnalyticsItem(lineItem, null), quantity }],
    },
  });
};

export const trackGA4ViewPromotion = ({
  promotionName,
  creativeSlot,
}: {
  promotionName: string;
  creativeSlot: string;
}) => {
  RbGtmClient.track({
    event: 'view_promotion',
    page_location: window.location.toString(),
    ecommerce: {
      creative_slot: creativeSlot,
      promotion_name: promotionName,
    },
  });
};

export const trackGA4SelectShippingPreference = ({
  shippingPreference,
}: {
  shippingPreference: string;
}) => {
  RbGtmClient.track({
    event: 'select_shipping_preference',
    page_location: window.location.toString(),
    shipping_preference: shippingPreference,
  });
};

export const trackGA4AddToCartEvent = ({
  cartLineItem,
  quantity,
  currency,
  pageType,
}: {
  cartLineItem: CartLineItemForGA4;
  quantity: number;
  currency: string;
  pageType: AnalyticsPageTypeProperty;
}) => {
  const lineItemsAddedTotal = (
    quantity * (cartLineItem.details.price?.amount || 0)
  ).toFixed(2);

  RbGtmClient.track({
    event: 'add_to_cart',
    page_location: window.location.toString(),
    page_type: pageType,
    context: AnalyticsContext.QuantitySelector,
    currency,
    ecommerce: {
      currency,
      value: lineItemsAddedTotal,
      items: [
        {
          ...mapCartLineItemToAnalyticsItem(cartLineItem, null),
          quantity,
        },
      ],
    },
    search_query_id: null,
  });
};

export const trackGA4AddGiftCardToCartEvent = ({
  giftCardAmount,
  currency,
  pageType,
}: {
  giftCardAmount: number;
  currency: string;
  pageType: AnalyticsPageTypeProperty;
}) => {
  RbGtmClient.track({
    event: 'add_to_cart',
    page_location: window.location.toString(),
    context: '',
    currency,
    page_type: pageType,
    ecommerce: {
      currency,
      value: giftCardAmount.toString(),
      items: [
        {
          work_id: null,
          product_type_id: '',
          blank_item_id: '',
          inventory_item_id: null,
          retargeting_id: null,
          item_id: `gift-certificate-${currency}-${giftCardAmount}`,
          item_list_id: null,
          item_list_name: null,
          discount: 0,
          index: null,
          price: giftCardAmount,
          quantity: 1,
        },
      ],
    },
    search_query_id: null,
  });
};
